<template>
  <div>
    <ConfirmationDialog
      v-model="showUnsavedDataDialog"
      :title="t('Unsaved changes')"
      :message="t('Your unsaved data will be lost if you continue')"
      confirm-text="Save Changes and Exit"
      cancel-text="Discard Changes and Exit"
      @confirm="saveChangesExit"
      @cancel="discardChangesExit"
      @close="closeChangesDialog"
    />
    <div class="pa-0 ml-auto mr-auto justify-center flex-column patient-form">
      <div class="form-title mb-6">
        {{ t(pageTitle) }}
      </div>
      <v-form
        ref="form"
        v-model="isFormValid"
        lazy-validation
        :disabled="!isEditable"
        @submit.prevent="onSubmit"
      >
        <div class="d-flex flex-column flex-sm-row">
          <InputText
            v-model="patient.firstName"
            class="col mr-3 pa-0"
            :label="t('First name') + '*'"
            :rules="[rules.required]"
            required
          />
          <InputText
            v-model="patient.lastName"
            class="col pa-0"
            :label="t('Last name') + '*'"
            :rules="[rules.required]"
            required
          />
        </div>

        <DatePicker
          v-model="dateOfBirthProxy"
          :label="t('Date of birth') + '*'"
          :rules="[rules.required]"
          :disabled="!isEditable"
        />

        <Dropdown
          v-model="chosenSexId"
          :label="t('Sex') + '*'"
          :items="sexes"
          :rules="[rules.required, sexValidation]"
          :item-title="(item: any) => item?.sexId ? t(convertFrontendSexValueToFormBuilderValue(item.sexId)) : ''"
          :item-value="'sexId'"
          :return-object="true"
          :disabled="!isEditable"
          hide-selected
        />
        <InputText
          v-model="patient.patientNumber"
          :label="t('CeGaT ID')"
          :disabled="hasRoleExternalUser"
          validate-on="blur"
        />
        <InputText
          v-model="patient.externalId"
          :label="t('External ID')"
        />
        <InputText
          v-model="patient.street"
          :label="t('Address')"
        />
        <div class="d-flex flex-column flex-sm-row">
          <InputText
            v-model="patient.postalCode"
            class="col mr-3 pa-0"
            :label="t('Postal code')"
          />
          <InputText
            v-model="patient.city"
            class="col pa-0"
            :label="t('City')"
          />
        </div>
        <InputText
          v-model="patient.country"
          :label="t('Country')"
          validate-on="blur"
        />
        <div class="d-flex">
          <Button
            class="flex-grow-1 mr-3"
            color="secondary"
            @click="cancel()"
          >
            {{ t("Cancel") }}
          </Button>
          <Button
            class="flex-grow-1 float-right"
            type="submit"
            :disabled="!isFormValid || !isEditable"
          >
            {{ t("Save") }}
          </Button>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Patient } from "@/entities/patient";
import { ref, reactive, computed, onMounted, watch } from 'vue';
import DatePicker from "@/components/input-components/DatePicker.vue";
import { Sex } from "@/entities/sex";
import Button from "@/components/input-components/Button.vue";
import InputText from "@/components/input-components/InputText.vue";
import Dropdown from "@/components/input-components/Dropdown.vue";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue";
import { convertFrontendSexValueToFormBuilderValue } from '@/utils'
import { usePermissionsService } from '@/composables/usePermissionsService';
import { usePatientsStore } from "@/stores/patients";
import { usePhysiciansStore } from '@/stores/physicians';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'PatientCreation'
})

const { t } = useI18n()
const emit = defineEmits(['save', 'cancel']);

const route = useRoute();
const permissionsService = usePermissionsService();
const patientsStore = usePatientsStore();
const physiciansStore = usePhysiciansStore();

const form = ref();
const isFormValid = ref(false);
const chosenSexId = ref<Sex | null>(null);
const showUnsavedDataDialog = ref(false);
const patientInfoHasChanged = ref(false);

const rules = {
  required: (value: string) => !!value || "Required.",
};

const getPatient = computed(() => patientsStore.getPatient);
const getPhysician = computed(() => physiciansStore.getPhysician);

const patientId = computed(() => {
  const id = route.params.patientId;
  return id ? Number(id) : 0;
});

const newPatient: Patient = {
  patientId: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  sexId: "u",
  street: "",
  postalCode: "",
  city: "",
  country: "",
  patientNumber: "",
  externalId: "",
};

const patient = reactive<Patient>({...newPatient});

const patientStringified = computed(() => JSON.stringify(patient));
const sexes = computed(() => patientsStore.getSexes);
const pageTitle = computed(() => (patientId.value ? "Patient" : "New Patient"));
const getPhysicianId = computed(() => getPhysician.value?.userId || "");
const hasRoleExternalUser = computed(() => permissionsService.hasRoleExternalUser || false);
const isNewPatient = computed(() => !patient.patientId);
// TODO: Editing of patient records by external users is not required in the near future.
// https://activecollab.tue.cegat.de/projects/1784?modal=Task-309140-1784
const isEditable = computed(() => isNewPatient.value || !hasRoleExternalUser.value);
const dateOfBirthProxy = computed({
  get: () => patient.dateOfBirth,
  set: (val: string) => patient.dateOfBirth = val
})

async function fetchPatient(): Promise<void> {
  if (patientId.value > 0) {
    await patientsStore.fetchPatient(
      patientId.value,
      getPhysicianId.value,
    );
    const patientData = patientsStore.patient;
    if (patientData) {
      Object.assign(patient, patientData);
    }
  }
}

function fetchSexes(): void {
  patientsStore.fetchPatientsSexes();
}

function sexValidation(value: Sex): boolean | string {
  return (
    sexes.value.some((s) => s.sexId === value?.sexId) ||
    "Value not accepted: " + value
  );
}

function saveChangesExit(): void {
  showUnsavedDataDialog.value = false;
}

function discardChangesExit(): void {
  showUnsavedDataDialog.value = false;
  emit("cancel");
}

function closeChangesDialog(): void {
  showUnsavedDataDialog.value = false;
}

function onSubmit() {
  const result = form.value?.validate()

  Promise.resolve(result).then((valid) => {
    if (valid) {
      patientInfoHasChanged.value = false
      emit("save", patient)
    }
  })
}

function cancel(): void {
  if (patientInfoHasChanged.value) {
    showUnsavedDataDialog.value = true;
  } else {
    emit("cancel");
  }
}

watch(patientStringified, (newPat, oldPat) => {
    // new patient being created or not first time edited
    // prevents detecting changes when the initial data is fetched
    if (newPat !== oldPat) {
      if (
        JSON.parse(oldPat).patientId > 0 ||
        JSON.parse(newPat).patientId === 0
      ) {
        patientInfoHasChanged.value = true;
      }
    }
  }
);

watch(chosenSexId, (newSex: any) => {
  patient.sexId = newSex?.sexId || '';
});

onMounted(async () => {
  fetchSexes();
  await fetchPatient();

  chosenSexId.value = sexes.value.find(s => s.sexId === patient.sexId) || null
});
</script>

<style lang="scss" scoped>
.patient-form {
  max-width: 400px;
}
</style>
